import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash, Copy as CopyIcon, Eye, FloppyDisk, X, UploadSimple,
  TextAa, Paragraph, ListBullets, CheckSquare, CaretDown, CalendarBlank, Hash,
  EnvelopeSimple, Phone, SlidersHorizontal, FileArrowUp, ShieldCheck, TextH,
  Image as ImageIcon, DotsSixVertical,
} from '@phosphor-icons/react';
import { DraggableList, InlineEdit } from '@/components/DraggableList';
import { FormRenderer } from '@/components/FormRenderer';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;
const imageUrl = (path) => `${API}/api/files/${path}`;

const FIELD_TYPES = [
  { type: 'short_text', label: 'Texto curto', icon: TextAa },
  { type: 'long_text', label: 'Texto longo', icon: Paragraph },
  { type: 'single_choice', label: 'Escolha única', icon: ListBullets },
  { type: 'multiple_choice', label: 'Escolha múltipla', icon: CheckSquare },
  { type: 'dropdown', label: 'Lista pendente', icon: CaretDown },
  { type: 'date', label: 'Data', icon: CalendarBlank },
  { type: 'number', label: 'Número', icon: Hash },
  { type: 'email', label: 'Email', icon: EnvelopeSimple },
  { type: 'phone', label: 'Telefone', icon: Phone },
  { type: 'scale', label: 'Escala', icon: SlidersHorizontal },
  { type: 'file_upload', label: 'Upload de ficheiro', icon: FileArrowUp },
  { type: 'consent_checkbox', label: 'Caixa de consentimento', icon: ShieldCheck },
];
const CONTENT_TYPES = [
  { type: 'section_title', label: 'Título de secção', icon: TextH },
  { type: 'content_text', label: 'Texto descritivo', icon: Paragraph },
  { type: 'content_image', label: 'Imagem', icon: ImageIcon },
];
const ALL_TYPES = [...FIELD_TYPES, ...CONTENT_TYPES];
const TYPE_META = Object.fromEntries(ALL_TYPES.map(t => [t.type, t]));
const CONTENT_BLOCK_TYPES = ['section_title', 'content_text', 'content_image'];

function newField(type) {
  return { id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, type, label: '', help_text: '', required: false, placeholder: '', options: type === 'single_choice' || type === 'multiple_choice' || type === 'dropdown' ? ['Opção 1'] : [], scale_max: 5, image_path: '' };
}

function AddFieldMenu({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="w-full py-3 rounded-xl border-2 border-dashed border-brand-border text-brand-muted hover:border-brand-green hover:text-brand-green transition-colors flex items-center justify-center gap-2 text-sm font-medium" data-testid="add-field-btn">
        <Plus size={16} /> Adicionar campo
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white rounded-2xl border border-brand-border shadow-lg p-2 max-h-80 overflow-y-auto">
          <p className="text-[10px] uppercase tracking-wider text-brand-muted px-2 pt-1 pb-1.5 font-semibold">Campos de resposta</p>
          {FIELD_TYPES.map(t => (
            <button key={t.type} onClick={() => { onAdd(t.type); setOpen(false); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-brand-bg text-sm text-brand-text text-left" data-testid={`add-field-type-${t.type}`}>
              <t.icon size={16} className="text-brand-green" /> {t.label}
            </button>
          ))}
          <p className="text-[10px] uppercase tracking-wider text-brand-muted px-2 pt-2 pb-1.5 font-semibold border-t border-brand-border/60 mt-1">Blocos de conteúdo</p>
          {CONTENT_TYPES.map(t => (
            <button key={t.type} onClick={() => { onAdd(t.type); setOpen(false); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-brand-bg text-sm text-brand-text text-left" data-testid={`add-field-type-${t.type}`}>
              <t.icon size={16} className="text-brand-gold-hover" /> {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldEditor({ field, onChange, onDuplicate, onDelete, dragHandleProps, uploadImage }) {
  const meta = TYPE_META[field.type];
  const isContentBlock = CONTENT_BLOCK_TYPES.includes(field.type);
  const hasOptions = ['single_choice', 'multiple_choice', 'dropdown'].includes(field.type);
  const patch = (p) => onChange({ ...field, ...p });

  return (
    <div className="bg-white rounded-2xl border border-brand-border p-4" data-testid={`field-editor-${field.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span {...dragHandleProps} data-testid={`drag-handle-${field.id}`} />
        <meta.icon size={15} className={isContentBlock ? 'text-brand-gold-hover' : 'text-brand-green'} />
        <span className="text-[11px] font-semibold text-brand-muted uppercase tracking-wide flex-1">{meta.label}</span>
        <button onClick={() => onDuplicate(field.id)} title="Duplicar campo" className="p-1.5 rounded-lg hover:bg-brand-bg text-brand-muted hover:text-brand-green" data-testid={`duplicate-field-${field.id}`}><CopyIcon size={14} /></button>
        <button onClick={() => onDelete(field.id)} title="Apagar campo" className="p-1.5 rounded-lg hover:bg-red-50 text-brand-muted hover:text-red-500" data-testid={`delete-field-${field.id}`}><Trash size={14} /></button>
      </div>

      <input value={field.label} onChange={e => patch({ label: e.target.value })} placeholder={field.type === 'content_text' ? 'Título (opcional)' : 'Rótulo do campo'}
        className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-green outline-none text-sm mb-2" data-testid={`field-label-${field.id}`} />

      {field.type === 'content_text' && (
        <textarea value={field.help_text} onChange={e => patch({ help_text: e.target.value })} placeholder="Texto descritivo" rows={3}
          className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-green outline-none text-sm mb-2" data-testid={`field-content-text-${field.id}`} />
      )}

      {!isContentBlock && (
        <input value={field.help_text} onChange={e => patch({ help_text: e.target.value })} placeholder="Texto de ajuda (opcional)"
          className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-green outline-none text-xs mb-2" data-testid={`field-help-${field.id}`} />
      )}

      {['short_text', 'long_text', 'number'].includes(field.type) && (
        <input value={field.placeholder} onChange={e => patch({ placeholder: e.target.value })} placeholder="Placeholder (texto de exemplo)"
          className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-green outline-none text-xs mb-2" data-testid={`field-placeholder-${field.id}`} />
      )}

      {field.type === 'consent_checkbox' && (
        <input value={field.placeholder} onChange={e => patch({ placeholder: e.target.value })} placeholder="Texto junto à caixa (ex: Aceito receber comunicações)"
          className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-green outline-none text-xs mb-2" data-testid={`field-consent-text-${field.id}`} />
      )}

      {hasOptions && (
        <div className="space-y-1.5 mb-2">
          {field.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input value={opt} onChange={e => patch({ options: field.options.map((o, j) => j === i ? e.target.value : o) })}
                className="flex-1 px-3 py-1.5 rounded-lg border border-brand-border focus:border-brand-green outline-none text-xs" data-testid={`field-option-${field.id}-${i}`} />
              <button onClick={() => patch({ options: field.options.filter((_, j) => j !== i) })} className="p-1 text-brand-muted hover:text-red-500" data-testid={`remove-option-${field.id}-${i}`}><X size={13} /></button>
            </div>
          ))}
          <button onClick={() => patch({ options: [...field.options, `Opção ${field.options.length + 1}`] })} className="text-xs text-brand-green hover:underline" data-testid={`add-option-${field.id}`}>+ Adicionar opção</button>
        </div>
      )}

      {field.type === 'scale' && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-brand-muted">Escala:</span>
          {[5, 10].map(n => (
            <button key={n} onClick={() => patch({ scale_max: n })} className={`px-3 py-1 rounded-full text-xs font-medium ${field.scale_max === n ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-text'}`} data-testid={`scale-max-${field.id}-${n}`}>1 a {n}</button>
          ))}
        </div>
      )}

      {field.type === 'content_image' && (
        <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-brand-border cursor-pointer hover:border-brand-green text-xs text-brand-muted" data-testid={`field-image-upload-${field.id}`}>
          <UploadSimple size={15} />
          {field.image_path ? 'Imagem carregada, clica para substituir' : 'Carregar imagem'}
          <input type="file" accept="image/*" className="hidden" onChange={async e => { const p = await uploadImage(e.target.files?.[0]); if (p) patch({ image_path: p }); }} />
        </label>
      )}
      {field.type === 'content_image' && field.image_path && (
        <img src={imageUrl(field.image_path)} alt="" className="mt-2 h-20 rounded-lg object-cover" />
      )}

      {!isContentBlock && (
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input type="checkbox" checked={field.required} onChange={e => patch({ required: e.target.checked })} className="w-3.5 h-3.5 accent-[#657161]" data-testid={`field-required-${field.id}`} />
          <span className="text-xs text-brand-muted">Obrigatório</span>
        </label>
      )}
    </div>
  );
}

export default function AdminFormBuilderPage() {
  useRequireAdmin();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/api/forms/${id}`).then(({ data }) => setForm(data)).catch(() => { toast.error('Formulário não encontrado'); navigate('/admin/formularios'); });
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const patch = (p) => { setForm(prev => ({ ...prev, ...p })); setDirty(true); };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...extra };
      const { data } = await axios.put(`${API}/api/forms/${id}`, payload);
      setForm(data);
      setDirty(false);
      toast.success('Guardado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível guardar');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return '';
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/api/upload`, fd);
      return data.path;
    } catch { toast.error('Erro ao carregar imagem'); return ''; }
  };

  const addField = (type) => patch({ fields: [...form.fields, newField(type)] });
  const updateField = (updated) => patch({ fields: form.fields.map(f => f.id === updated.id ? updated : f) });
  const duplicateField = (fid) => {
    const f = form.fields.find(x => x.id === fid);
    const idx = form.fields.findIndex(x => x.id === fid);
    const copy = { ...f, id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` };
    const arr = [...form.fields]; arr.splice(idx + 1, 0, copy);
    patch({ fields: arr });
  };
  const deleteField = (fid) => patch({ fields: form.fields.filter(f => f.id !== fid) });

  const copyLink = () => { navigator.clipboard.writeText(form.public_url); toast.success('Link copiado'); };

  if (!form) return <p className="text-sm text-brand-muted">A carregar...</p>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-form-builder-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate('/admin/formularios')} className="flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-green" data-testid="back-to-forms-btn">
          <ArrowLeft size={16} /> Formulários
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPreview(true)} className="px-3.5 py-2 rounded-full border border-brand-border text-sm text-brand-text flex items-center gap-1.5 hover:bg-brand-bg" data-testid="preview-form-btn"><Eye size={15} /> Pré-visualizar</button>
          <button onClick={() => save()} disabled={!dirty || saving} className="px-4 py-2 rounded-full bg-brand-green text-white text-sm font-medium flex items-center gap-1.5 hover:bg-brand-green-hover disabled:opacity-50" data-testid="save-form-btn">
            <FloppyDisk size={15} /> {saving ? 'A guardar...' : 'Guardar alterações'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-border p-5 space-y-3">
        <InlineEdit value={form.title} onSave={v => save({ title: v })} className="font-heading text-xl text-brand-green font-semibold" testId="form-title-inline" />
        <div className="flex items-center gap-2 flex-wrap">
          {['rascunho', 'publicado', 'fechado'].map(s => (
            <button key={s} onClick={() => save({ status: s })} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${form.status === s ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid={`status-btn-${s}`}>{s}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-brand-muted">Link:</span>
          <code className="text-brand-text bg-brand-bg px-2 py-1 rounded truncate flex-1">{form.public_url}</code>
          <button onClick={copyLink} className="text-brand-green hover:underline" data-testid="builder-copy-link-btn">Copiar</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-border p-5 space-y-3">
        <p className="text-[11px] uppercase tracking-wider text-brand-muted font-semibold">Definições</p>
        <textarea value={form.description} onChange={e => patch({ description: e.target.value })} placeholder="Descrição (mostrada no topo do formulário)" rows={2}
          className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-green outline-none text-sm" data-testid="form-description-input" />
        <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-brand-border cursor-pointer hover:border-brand-green text-xs text-brand-muted w-fit" data-testid="cover-image-upload">
          <UploadSimple size={15} /> {form.cover_image_path ? 'Substituir imagem de capa' : 'Carregar imagem de capa'}
          <input type="file" accept="image/*" className="hidden" onChange={async e => { const p = await uploadImage(e.target.files?.[0]); if (p) patch({ cover_image_path: p }); }} />
        </label>
        {form.cover_image_path && <img src={imageUrl(form.cover_image_path)} alt="" className="h-24 rounded-lg object-cover" />}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-brand-muted block mb-1">Data de abertura</label>
            <DateField value={form.opens_at || ''} onChange={e => patch({ opens_at: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="opens-at-input" />
          </div>
          <div>
            <label className="text-xs text-brand-muted block mb-1">Data de fecho</label>
            <DateField value={form.closes_at || ''} onChange={e => patch({ closes_at: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="closes-at-input" />
          </div>
        </div>
        <div>
          <label className="text-xs text-brand-muted block mb-1">Limite máximo de respostas (opcional)</label>
          <input type="number" min="1" value={form.max_responses || ''} onChange={e => patch({ max_responses: e.target.value ? parseInt(e.target.value) : null })} className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="max-responses-input" />
        </div>
        <div>
          <label className="text-xs text-brand-muted block mb-1">Mensagem de agradecimento</label>
          <textarea value={form.thank_you_message} onChange={e => patch({ thank_you_message: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="thank-you-message-input" />
        </div>
        <div>
          <label className="text-xs text-brand-muted block mb-1">Mensagem quando fechado / esgotado</label>
          <textarea value={form.closed_message} onChange={e => patch({ closed_message: e.target.value })} rows={2} placeholder="Ex: As candidaturas para este evento já encerraram." className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="closed-message-input" />
        </div>
        <div>
          <label className="text-xs text-brand-muted block mb-1">Texto de consentimento RGPD (editar altera a versão gravada)</label>
          <textarea value={form.consent_text} onChange={e => patch({ consent_text: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="consent-text-input" />
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-[11px] uppercase tracking-wider text-brand-muted font-semibold px-1">Campos</p>
        <DraggableList
          items={form.fields}
          onReorder={(arr) => patch({ fields: arr })}
          keyExtractor={(f) => f.id}
          className="space-y-3"
          renderItem={(f, idx, dragHandleProps) => (
            <FieldEditor field={f} onChange={updateField} onDuplicate={duplicateField} onDelete={deleteField} dragHandleProps={dragHandleProps} uploadImage={uploadImage} />
          )}
        />
        <AddFieldMenu onAdd={addField} />
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowPreview(false)}>
          <div className="bg-brand-bg rounded-3xl p-6 max-w-md w-full my-8" onClick={e => e.stopPropagation()} data-testid="preview-modal">
            <div className="flex justify-end mb-2"><button onClick={() => setShowPreview(false)} data-testid="close-preview-btn"><X size={18} className="text-brand-muted" /></button></div>
            <div className="bg-white rounded-3xl border border-brand-border p-6">
              <h1 className="font-heading text-xl text-brand-green font-semibold mb-1.5">{form.title}</h1>
              {form.description && <p className="text-sm text-brand-muted mb-4 whitespace-pre-wrap">{form.description}</p>}
              <FormRenderer form={form} imageUrl={imageUrl} preview onSubmit={() => toast.info('Isto é uma pré-visualização')} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
